/**
 * Repository for reading AI prediction data from Firestore.
 *
 * Used by Phase 6 (device list) to read `aiPrediction.latest`
 * directly from chain documents — no API call needed for the device list.
 *
 * The backend pre-resolves `aiPrediction.latest` so the frontend does NOT
 * need to implement the 3-day correction expiry rule.
 *
 * Firestore path: `comboxes/{serialNo}/chains/{chainId}`
 * Field: `aiPrediction.latest`
 *
 * Reference: ml-predictor-reference/frontend-requirements.md §5
 */
import { db } from "@/main";
import {
  AiPredictionField,
  AiPredictionLatest,
  CorrectionDocument,
  DailyPrediction,
  DailySummary,
  LatestSummary,
  ManualPrediction,
  PredictionsResponse,
  TrafficLight,
  WindowCorrection,
} from "@/data/models/AiPrediction";

/**
 * Get the Firestore reference for a chain document.
 *
 * @param serialNo - ComBox serial number
 * @param chainId - Program chain ID
 * @returns Firestore document reference
 */
function getChainRef(serialNo: string, chainId: string) {
  return db
    .collection("comboxes")
    .doc(serialNo)
    .collection("chains")
    .doc(chainId);
}

/**
 * Read the AI prediction summary for a specific chain.
 *
 * Returns the pre-resolved `aiPrediction.latest` field from the chain document.
 * Returns `null` if no AI prediction data exists for the chain.
 *
 * @param serialNo - ComBox serial number
 * @param chainId - Program chain ID
 * @returns The latest AI prediction summary, or null
 */
export async function getAiPredictionLatest(
  serialNo: string,
  chainId: string
): Promise<AiPredictionLatest | null> {
  try {
    const doc = await getChainRef(serialNo, chainId).get();

    if (!doc.exists) {
      return null;
    }

    const data = doc.data();
    const aiPrediction = data?.aiPrediction as AiPredictionField | undefined;

    return aiPrediction?.latest ?? null;
  } catch {
    // Non-critical read failure — the device-list border simply won't show.
    return null;
  }
}

/**
 * Get the traffic light value for a chain.
 *
 * Convenience method that returns just the traffic light string,
 * defaulting to "UNKNOWN" if no data exists.
 *
 * @param serialNo - ComBox serial number
 * @param chainId - Program chain ID
 * @returns Traffic light value
 */
export async function getTrafficLight(
  serialNo: string,
  chainId: string
): Promise<TrafficLight> {
  const latest = await getAiPredictionLatest(serialNo, chainId);
  return latest?.trafficLight ?? "UNKNOWN";
}

/**
 * Observe real-time changes to the AI prediction summary for a chain.
 *
 * Sets up a Firestore snapshot listener for the chain document and
 * calls `onUpdate` whenever the `aiPrediction.latest` field changes.
 *
 * @param serialNo - ComBox serial number
 * @param chainId - Program chain ID
 * @param onUpdate - Callback receiving the latest AI prediction summary (or null)
 * @returns Unsubscribe function to stop listening
 */
export function observeAiPredictionLatest(
  serialNo: string,
  chainId: string,
  onUpdate: (latest: AiPredictionLatest | null) => void
): () => void {
  return getChainRef(serialNo, chainId).onSnapshot(
    (snapshot) => {
      if (!snapshot.exists) {
        onUpdate(null);
        return;
      }

      const data = snapshot.data();
      const aiPrediction = data?.aiPrediction as AiPredictionField | undefined;
      onUpdate(aiPrediction?.latest ?? null);
    },
    () => {
      // Non-critical listener error — treat as no prediction data.
      onUpdate(null);
    }
  );
}

/**
 * Read a specific daily prediction by its execution ID from Firestore.
 *
 * Firestore path: `comboxes/{serialNo}/chains/{chainId}/aiPredictions/{executionId}`
 *
 * @param serialNo - ComBox serial number
 * @param chainId - Program chain ID
 * @param executionId - The prediction execution ID (e.g. "daily_2026-04-29_mixed_v2_2026-05-11")
 * @returns The full daily prediction document, or null if not found
 */
export async function getDailyPredictionByExecutionId(
  serialNo: string,
  chainId: string,
  executionId: string
): Promise<DailyPrediction | null> {
  try {
    const doc = await db
      .collection("comboxes")
      .doc(serialNo)
      .collection("chains")
      .doc(chainId)
      .collection("aiPredictions")
      .doc(executionId)
      .get();

    if (!doc.exists) return null;
    return doc.data() as DailyPrediction;
  } catch {
    // Non-critical read failure — caller treats a missing doc as no data.
    return null;
  }
}

/**
 * Load the full predictions payload for the Quality page from Firestore.
 *
 * Reads:
 * 1. Chain doc `aiPrediction` field → summary (daily + latest + correction)
 * 2. Latest daily prediction from subcollection → daily
 * 3. Manual prediction from subcollection → manual
 * 4. Correction doc from corrections subcollection → correction
 *
 * Returns null if the chain has no `aiPrediction` field.
 *
 * @param serialNo - ComBox serial number
 * @param chainId - Program chain ID
 * @returns PredictionsResponse constructed from Firestore, or null
 */
export async function getPredictionsFromFirestore(
  serialNo: string,
  chainId: string
): Promise<PredictionsResponse | null> {
  try {
    const chainDoc = await getChainRef(serialNo, chainId).get();
    if (!chainDoc.exists) return null;

    const chainData = chainDoc.data();
    const aiPrediction = chainData?.aiPrediction as AiPredictionField | undefined;
    if (!aiPrediction?.daily && !aiPrediction?.latest) return null;

    const predsRef = getChainRef(serialNo, chainId).collection("aiPredictions");

    // Build summary from chain doc's aiPrediction field
    const dailySummary: DailySummary = {
      trafficLight: aiPrediction.daily?.trafficLight ?? aiPrediction.latest?.trafficLight ?? "UNKNOWN",
      isDry: aiPrediction.daily?.isDry ?? false,
      isStationary: aiPrediction.daily?.isStationary ?? false,
      latestExecutionId: aiPrediction.daily?.latestExecutionId ?? "",
      source: "daily",
    };

    const latestSummary: LatestSummary = {
      trafficLight: aiPrediction.latest?.trafficLight ?? "UNKNOWN",
      source: aiPrediction.latest?.source ?? "daily",
      modelVersion: aiPrediction.latest?.modelVersion,
    };

    // Read latest daily prediction from subcollection
    let daily: DailyPrediction | null = null;
    if (dailySummary.latestExecutionId) {
      daily = await getDailyPredictionByExecutionId(serialNo, chainId, dailySummary.latestExecutionId);
    }

    // Read manual prediction (stored at doc ID "manual")
    let manual: ManualPrediction | null = null;
    try {
      const manualDoc = await predsRef.doc("manual").get();
      if (manualDoc.exists) {
        manual = manualDoc.data() as ManualPrediction;
      }
    } catch {
      // Manual prediction not found — not an error
    }

    // Read correction document (stored in corrections subcollection)
    let correction: CorrectionDocument | null = null;
    const correctionSummary: WindowCorrection | undefined = aiPrediction.correction;
    try {
      const corrDocs = await getChainRef(serialNo, chainId).collection("corrections").get();
      if (!corrDocs.empty) {
        const corrections: Record<string, WindowCorrection> = {};
        corrDocs.docs.forEach(doc => {
          corrections[doc.id] = doc.data() as WindowCorrection;
        });
        correction = { corrections };
      }
    } catch {
      // Corrections not found — not an error
    }

    return {
      chainId,
      serialNo,
      daily,
      manual,
      correction,
      summary: {
        daily: dailySummary,
        latest: latestSummary,
        correction: correctionSummary,
      },
    };
  } catch {
    // Non-critical read failure — the Quality page falls back to the API.
    return null;
  }
}
